import React, { memo, useState } from 'react'

// SVG triangle icon for toggle
const TriangleIcon = ({ direction = 'up', color = '#0a1847', size = 20 }) => {
  const rotation = direction === 'up' ? '0' : '180';
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" style={{ transform: `rotate(${rotation}deg)` }}>
      <polygon points="10,5 17,15 3,15" fill={color} />
    </svg>
  )
}

interface FlowControlsProps {
  setDebug: (value: React.SetStateAction<boolean>) => void
  newNode: () => void
  setShowMap: (value: React.SetStateAction<boolean>) => void
  onCreatingAnnotationNode: () => void
  createCustomNode: () => void
  createQuickStartNode: () => void
  onCreatingRandomSequenceNode: () => void
  createDocumentNode: () => void
  createChatNode: () => void
  createCompareFilesNode: () => void
  createSmartNotesNode: () => void
  createDocumentDesignerNode: () => void
  createNodeIntellica: () => void
  createNodeDiagrams: () => void
  createResearcherNode: () => void
  createFileMinerNode: () => void
  createJavascriptNode: () => void
  createImageGeneratorNode: () => void
  createNodeResource: () => void
  // Add autolayout functions
  applyAutoLayout?: () => void
  applyHorizontalLayout?: () => void
  applyVerticalLayout?: () => void
  applyGridLayout?: () => void
  applyOptimizedGridLayout?: () => void // <-- add this
}

const BUTTONS = [
  { onClickKey: 'setDebug', title: 'Toggle Debug', label: '?' },
  { onClickKey: 'setShowMap', title: 'Toggle Map', label: 'M' },
  // Add autolayout buttons with string labels instead of JSX components
  { onClickKey: 'applyAutoLayout', title: 'Auto Layout (Tree)', label: '🌳' },
  { onClickKey: 'applyVerticalLayout', title: 'Vertical Layout', label: '⬇' },
  { onClickKey: 'applyHorizontalLayout', title: 'Horizontal Layout', label: '➡' },
  { onClickKey: 'applyGridLayout', title: 'Grid Layout (5 per row)', label: '⊞' },
  { onClickKey: 'applyOptimizedGridLayout', title: 'Optimized Grid Layout', label: '▦' }, // <-- new button
]

const FlowControls: React.FC<FlowControlsProps> = (props) => {
  const [expanded, setExpanded] = useState(false)

  // Only show the toggle button if not expanded
  return (
    <div className={`custom-flow-controls${expanded ? ' expanded' : ''}`}>
      {/* Toggle button always visible */}
      <button
        className="topbar-glass-btn flowcontrols-toggle-btn"
        title={expanded ? 'Collapse controls' : 'Expand controls'}
        style={{ background: 'rgba(10,24,71,0.85)', border: '1.5px solid #0a1847', color: '#ffffffd0', zIndex: 2 }}
        onClick={() => setExpanded((v) => !v)}
        type="button"
      >
        <TriangleIcon direction={expanded ? 'down' : 'up'} color="#FFFFFFb0" size={16} />
      </button>
      {/* Animated menu of buttons */}
      <div className={`flowcontrols-buttons-container${expanded ? ' show' : ''}`}>
        {BUTTONS.map((btn, idx) => {
          // Map prop function
          let onClick = props[btn.onClickKey as keyof FlowControlsProps] as any
          if (btn.onClickKey === 'setDebug' || btn.onClickKey === 'setShowMap') {
            // These expect a function that takes prev => !prev
            onClick = () => (props[btn.onClickKey as keyof FlowControlsProps] as any)((prev: boolean) => !prev)
          }
          
          // Skip rendering if the function doesn't exist (for optional autolayout functions)
          if (!onClick) return null
          
          return (
            <button
              key={typeof btn.label === 'string' ? btn.label : idx}
              className="topbar-glass-btn flowcontrols-action-btn"
              title={btn.title}
              style={{
                background: 'rgba(10,24,71,0.22)',
                border: '1.5px solid #0a184760',
                color: '#ffffffd0',
                transitionDelay: expanded ? `${idx * 30}ms` : `${(BUTTONS.length-idx) * 10}ms`,
              }}
              onClick={onClick}
              type="button"
              tabIndex={expanded ? 0 : -1}
            >
              {btn.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default memo(FlowControls)
